<script setup lang="ts">
/**
 * 列表上方的看板壳：折叠开关 + 下钻提示条 + ModuleDashboard。
 *
 * 这三样原来只写在模块中心（ModuleWorkbenchView）里。把看板下沉到项目页时，
 * 如果照抄一遍，就又变成「改一边另一边不动」——记录抽屉刚为这件事收敛过一轮，
 * 这里不重蹈。壳做成组件，下钻状态用 v-model 交回给列表。
 */
import { computed } from 'vue';

import ModuleDashboard from '@/components/ModuleDashboard.vue';
import type { ModuleRecord } from '@/types/domain';
import { statusText } from '@/utils/format';
import { dashboardConfig, type DashboardScope, type DrillFilter } from '@/utils/module-dashboard';

const props = withDefaults(defineProps<{
  moduleKey: string;
  entityName: string;
  /** 全量记录（未经下钻筛选）——图要按全量画，否则点一次就再也回不去 */
  items: ModuleRecord[];
  /** 下钻生效后列表还剩几条，显示在提示条上 */
  filteredCount: number;
  modelValue: DrillFilter | null;
  /** 默认收起（1027 审查 P2-10）：执行页先给清单，要看图点「查看看板」 */
  open?: boolean;
  scope?: DashboardScope;
  /**
   * 少于这么多条就不画图。
   * 单个项目里常常只有三五条，三根一样长的柱子比没有图更糟——
   * 「界面上有、库里没数据」这个坑踩过一次就够了。0 表示不设闸。
   */
  minItems?: number;
  /** 项目 id →「项目号 · 名称」，原样交给 ModuleDashboard：图和清单用同一份项目名（1027 复评 r2） */
  projectLabels?: Record<string, string>;
  /** 变更类型 ID → 中文名，同上 */
  changeTypeLabels?: Record<string, string>;
}>(), {
  open: false,
  scope: 'global',
  minItems: 0,
});

const emit = defineEmits<{
  'update:modelValue': [filter: DrillFilter | null];
  'update:open': [open: boolean];
}>();

const available = computed(() => Boolean(dashboardConfig(props.moduleKey, props.scope)));
/** 数据太薄：不画图，但要说一句为什么，不能悄悄什么都不显示 */
const tooThin = computed(() => props.minItems > 0 && props.items.length < props.minItems);
/**
 * 提示条上写的筛选值（1028 Soft-Gate r3-p3-tail）：按状态下钻时 value 是原始状态码（rejected），
 * 柱子上已用 statusText 翻成「已退回」，提示条跟柱子说同一个词；其余维度的 value 本来就是柱子名，原样显示。
 * 只改显示，筛选仍按原始值走。
 */
const drillLabel = computed(() => {
  const filter = props.modelValue;
  if (!filter) return '';
  return filter.kind === 'status' ? statusText(filter.value) : filter.value;
});
</script>

<template>
  <template v-if="available">
    <p v-if="tooThin" class="dashboard-thin">
      本项目只有 {{ items.length }} 个{{ entityName }}，画出来就是几根一样长的柱子，直接看下面的清单。
    </p>
    <template v-else>
      <div class="cd-toggle">
        <button type="button" @click="emit('update:open', !open)">
          {{ open ? '收起看板' : '查看看板' }}
        </button>
        <span v-if="modelValue" class="cd-drill">
          已按「{{ drillLabel }}」筛选，{{ filteredCount }} 条
          <button type="button" @click="emit('update:modelValue', null)">清除</button>
        </span>
      </div>
      <ModuleDashboard
        v-if="open"
        :module-key="moduleKey"
        :entity-name="entityName"
        :items="items"
        :scope="scope"
        :project-labels="projectLabels"
        :change-type-labels="changeTypeLabels"
        @drill="emit('update:modelValue', $event)"
      />
    </template>
  </template>
</template>

<style scoped>
.cd-toggle { display: flex; align-items: center; gap: 10px; margin-bottom: 10px; }
.cd-toggle > button {
  padding: 5px 12px; border: 1px solid var(--line); background: var(--white);
  color: var(--blue); font: 700 12px var(--body); cursor: pointer;
}
.cd-toggle > button:hover { border-color: var(--blue); }
.cd-drill {
  display: inline-flex; align-items: center; gap: 6px; padding: 3px 8px;
  background: #EEF4FA; color: var(--blue); font-size: 12px;
}
.cd-drill button { min-height: 24px; display: inline-flex; align-items: center; padding: 0 8px; border: 1px solid var(--line-strong); border-radius: var(--radius-s); background: var(--white); cursor: pointer; font: inherit; font-size: 12px; font-weight: 700; line-height: 1; white-space: nowrap; color: var(--blue); }
.cd-drill button:hover { border-color: var(--blue); background: rgba(0,117,174,.07); }
.dashboard-thin { margin: 0 0 10px; color: var(--muted); font-size: 12px; line-height: 1.6; }
</style>
